import { useRef, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import {
  useCircleTexture,
  useMousePosition,
  makeDriftOnBeforeCompile,
  makePhases,
  makeUniformDrift,
  makeZeroVec3,
} from "./particleHooks";
import DragonPoints from "./DragonParticles";
import { useRevealed } from "../revealContext";
import { devTuningSearch } from "../dragonParams";

const AMBIENT_DEFAULTS = {
  /* Raised alongside the wider margin so on-screen density holds steady: the
     extra overscan area would otherwise thin out what the camera actually sees. */
  count: 23000,
  size: 0.024,
  opacity: 0.9,
  /* Particles are distributed in screen space rather than a fixed world-space
     box, so the field fills the frame exactly at any viewport size and none of
     the budget is spent off-camera. */
  depth: 3, // thickness of the slab; depth spread is what parallax reads from
  margin: 1.25, // overscan past the frame, giving the motion room to travel
  /* Rotation, not translation, is what makes the field feel three-dimensional:
     it swings particles through depth so near and far ones separate on screen.
     Kept as bounded oscillation rather than an accumulating spin, which would
     eventually turn the frame-fitted slab edge-on and bare the corners. */
  tilt: 0.14, // radians the field turns toward the cursor
  swing: 0.05, // radians of idle rotation
  parallax: 0.12, // how far the field slides with the cursor, in world units
  drift: 0.05, // amplitude of the idle float, in world units
  /* Per-particle wander, independent of the group motion above. */
  float: 0.09, // how far each particle strays from its home, in world units
  floatSpeed: 0.45,
  /* Tinted to match the dragon, but at roughly half its saturation: the field
     should register as the same colour family without competing with the figure
     for attention. Values are sRGB lightness for setHSL, so they read higher
     than the raw linear numbers they replaced. */
  hue: 0.6,
  saturation: 0.4,
  shadeDark: 0.52,
  shadeMid: 0.66,
  shadeLight: 0.78,
};

/* Development-only `?ambient...` overrides for the dials above, mirroring the
   dragon's `?dragon...` params. Production builds ignore the query string (see
   devTuningSearch in dragonParams.js) so the live site always renders the
   committed defaults. */
function readAmbientParams() {
  if (typeof window === "undefined") return AMBIENT_DEFAULTS;
  const q = new URLSearchParams(devTuningSearch());
  const num = (key, fallback) => {
    const raw = q.get(`ambient${key[0].toUpperCase()}${key.slice(1)}`);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  return {
    count: num("count", AMBIENT_DEFAULTS.count),
    size: num("size", AMBIENT_DEFAULTS.size),
    opacity: num("opacity", AMBIENT_DEFAULTS.opacity),
    shadeDark: num("shadeDark", AMBIENT_DEFAULTS.shadeDark),
    shadeMid: num("shadeMid", AMBIENT_DEFAULTS.shadeMid),
    shadeLight: num("shadeLight", AMBIENT_DEFAULTS.shadeLight),
    hue: num("hue", AMBIENT_DEFAULTS.hue),
    saturation: num("saturation", AMBIENT_DEFAULTS.saturation),
    depth: num("depth", AMBIENT_DEFAULTS.depth),
    margin: num("margin", AMBIENT_DEFAULTS.margin),
    parallax: num("parallax", AMBIENT_DEFAULTS.parallax),
    drift: num("drift", AMBIENT_DEFAULTS.drift),
    tilt: num("tilt", AMBIENT_DEFAULTS.tilt),
    swing: num("swing", AMBIENT_DEFAULTS.swing),
    float: num("float", AMBIENT_DEFAULTS.float),
    floatSpeed: num("floatSpeed", AMBIENT_DEFAULTS.floatSpeed),
  };
}

function Particles() {
  const pointsRef = useRef();
  const mouse = useMousePosition();
  const circleTexture = useCircleTexture();
  const autoSpin = useRef(0);
  const mouseOffset = useRef({ x: 0, y: 0 });
  const ambient = useRef(readAmbientParams()).current;
  const { viewport, camera } = useThree();

  const [positions, colors, phases, drifts, emits, normals] = useMemo(() => {
    const count = Math.max(1, Math.floor(ambient.count));
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const phases = makePhases(count);
    /* Shades are sRGB lightness; three works in linear space, so the colour
       space is named explicitly rather than letting the value pass through as
       already-linear. */
    const tint = new THREE.Color();
    const palette = [
      ambient.shadeDark,
      ambient.shadeMid,
      ambient.shadeLight,
    ].map((lightness) => {
      tint.setHSL(
        ambient.hue,
        ambient.saturation,
        Math.min(1, Math.max(0.02, lightness)),
        THREE.SRGBColorSpace,
      );
      return [tint.r, tint.g, tint.b];
    });

    /* viewport.width/height describe the frame at z = 0. The frustum widens
       with distance, so each particle's extent is scaled by its own depth;
       picking x and y per-particle that way fills the screen uniformly instead
       of inscribing a box that the camera only partly sees. */
    const cameraZ = camera.position.z;
    const halfWidth0 = (viewport.width / 2) * ambient.margin;
    const halfHeight0 = (viewport.height / 2) * ambient.margin;

    for (let i = 0; i < count; i++) {
      // shallow depth: a wide z range swings size attenuation hard enough that
      // near particles read as out-of-focus blobs over the dragon
      const z = (Math.random() - 0.5) * ambient.depth;
      const depthScale = (cameraZ - z) / cameraZ;

      positions[i * 3] = (Math.random() * 2 - 1) * halfWidth0 * depthScale;
      positions[i * 3 + 1] = (Math.random() * 2 - 1) * halfHeight0 * depthScale;
      positions[i * 3 + 2] = z;

      const [r, g, b] = palette[Math.floor(Math.random() * palette.length)];
      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
    }

    return [
      positions,
      colors,
      phases,
      makeUniformDrift(count, 1),
      new Float32Array(count),
      makeZeroVec3(count),
    ];
  }, [ambient, viewport.width, viewport.height, camera]);

  const driftShader = useRef(null);
  /* Built imperatively so onBeforeCompile is attached before the first compile;
     applied as a prop it can arrive after the program is already built, and the
     injection is then silently skipped. */
  const material = useMemo(() => {
    const m = new THREE.PointsMaterial({
      size: ambient.size,
      map: circleTexture,
      alphaTest: 0.25,
      vertexColors: true,
      transparent: true,
      opacity: ambient.opacity,
      sizeAttenuation: true,
      depthWrite: false,
    });
    m.onBeforeCompile = makeDriftOnBeforeCompile(
      driftShader,
      ambient.float,
      ambient.floatSpeed,
    );
    m.customProgramCacheKey = () => "ambient-points-drift";
    return m;
  }, [ambient, circleTexture]);

  /* Rotation carries the depth cue, translation only softens it. Both stay
     inside the margin overscan: at the frame edge a tilt of ~0.19 rad swings a
     particle far enough in z to grow about 20% on screen, which is what the 25%
     overscan is sized for. */
  useFrame((state, delta) => {
    if (!pointsRef.current) return;

    autoSpin.current += delta * 0.06;
    if (driftShader.current) driftShader.current.uniforms.uTime.value += delta;

    mouseOffset.current.x += (mouse.current.x - mouseOffset.current.x) * 0.03;
    mouseOffset.current.y += (mouse.current.y - mouseOffset.current.y) * 0.03;

    const idleX = Math.sin(autoSpin.current);
    const idleY = Math.cos(autoSpin.current * 0.7);

    // vertical cursor movement tips the field about X, horizontal about Y
    pointsRef.current.rotation.x =
      -mouseOffset.current.y * ambient.tilt + idleY * ambient.swing;
    pointsRef.current.rotation.y =
      mouseOffset.current.x * ambient.tilt + idleX * ambient.swing;

    pointsRef.current.position.x =
      mouseOffset.current.x * ambient.parallax + idleX * ambient.drift;
    pointsRef.current.position.y =
      mouseOffset.current.y * ambient.parallax * 0.7 + idleY * ambient.drift;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-aPhase"
          count={phases.length}
          array={phases}
          itemSize={1}
        />
        {/* the field has no core/halo split, so every particle drifts fully */}
        <bufferAttribute
          attach="attributes-aDrift"
          count={drifts.length}
          array={drifts}
          itemSize={1}
        />
        {/* the field never emits, but the shared shader declares these */}
        <bufferAttribute
          attach="attributes-aEmit"
          count={emits.length}
          array={emits}
          itemSize={1}
        />
        <bufferAttribute
          attach="attributes-aNormal"
          count={normals.length / 3}
          array={normals}
          itemSize={3}
        />
      </bufferGeometry>
      <primitive object={material} attach="material" />
    </points>
  );
}

const ParticleField = () => {
  /* Read on this side of the <Canvas> and handed down as a prop. Everything
     below <Canvas> is rendered by the r3f reconciler rather than react-dom, so
     a `useRevealed()` call in there is reaching across a root boundary. r3f
     does bridge context, but the default value of RevealContext is `true`, so
     if the bridge ever missed the dragon would decide it had been revealed and
     play its whole entrance behind the closed intro overlay - the exact failure
     this animation exists to avoid. The prop has no default, so an omitted one
     reads as undefined and the swirl policy coerces that to not-revealed: the
     failure is loud rather than plausible. */
  const revealed = useRevealed();

  return (
    <div className="absolute inset-0 -z-10 pointer-events-none">
      <Canvas
        dpr={[1, 2]}
        gl={{ alpha: true, antialias: true }}
        camera={{ position: [0, 0, 5], fov: 50 }}
      >
        <Particles />
        <DragonPoints revealed={revealed} />
      </Canvas>
    </div>
  );
};

export default ParticleField;
